import { useState, useEffect, useCallback, useMemo } from "react";
import ReviewForm from "./Forms/ReviewForm";
import RoomAdonsForm from "./Forms/RoomAdonsForm";
import RoomForm from "./Forms/RoomForm";
// import RoomInclusionForm from "./Forms/RoomInclusionForm";
import RoomTypeDrawer from "./RoomTypeDrawer";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import {
  FiFileText,
  FiCheckCircle,
  FiPlusCircle,
  FiCheck,
} from "react-icons/fi";
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import { HiArrowSmLeft } from "react-icons/hi";
import PriceSurchargeForm from "./Forms/PriceSurchargeForm";
import { markSetupStepCompleteById } from "../../utils/vendorSetupSteps";
import { useProfile } from "../../context/ProfileContext";
import { createPortal } from "react-dom";



const RoomDrawer = ({ isOpen, roomId, onClose, onSaved }) => {

  const [activeTab, setActiveTab] = useState("service");
  const [currentStep, setCurrentStep] = useState(0);
  const [existingServiceNames, setExistingServiceNames] = useState([]);
  const [isServiceFormValid, setIsServiceFormValid] = useState(false);
  const [isPricingSurchargeFormValid, setIsPricingSurchargeFormValid] =
    useState(true);
  const [tabProgress, setTabProgress] = useState({
    service: 0,
    // inclusion: 0,
    pricingSurcharge: 0,
    // adons: 0,
    review: 0,
  });

  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [adonServices, setAdonServices] = useState([]);
  const [currentTab, setCurrentTab] = useState("service");
  const [isRoomTypeDrawerOpen, setIsRoomTypeDrawerOpen] = useState(false);
  const [roomListVersion, setRoomListVersion] = useState(0);
  const { profileData, vendorId } = useProfile();
  // const [serviceCategory, setServiceCategory] = useState("");

  // Callback passed to child to update current tab
  const handleTabChange = (tabId) => {
    setCurrentTab(tabId);
  };

  const handleAddRoom = useCallback(() => {
    setIsRoomTypeDrawerOpen(true);
  }, []);

  const handleRoomSaved = useCallback((savedRoom) => {
    setIsRoomTypeDrawerOpen(false);
    setRoomListVersion((prev) => prev + 1);
    const savedRoomId =
      savedRoom?._id || savedRoom?.id || savedRoom?.room_type_id;

    if (savedRoomId) {
      setFormData((prev) => {
        const existingIds = Array.isArray(prev.service.selectedRoomTypes)
          ? prev.service.selectedRoomTypes.map((room) =>
              typeof room === "object"
                ? room._id || room.id || room.room_type_id
                : room,
            )
          : [];
        const nextRoomIds = existingIds.includes(savedRoomId)
          ? existingIds
          : [...existingIds, savedRoomId];
        const nextServiceRoomTypeOptions = {
          ...(prev.service.roomTypeOptions || {}),
          [savedRoomId]:
            prev.service.roomTypeOptions?.[savedRoomId] ??
            prev.pricingSurcharge?.roomTypeOptions?.[savedRoomId] ??
            "0",
        };
        const nextPricingRoomTypeOptions = {
          ...(prev.pricingSurcharge?.roomTypeOptions || {}),
          [savedRoomId]:
            prev.pricingSurcharge?.roomTypeOptions?.[savedRoomId] ??
            prev.service.roomTypeOptions?.[savedRoomId] ??
            "0",
        };

        return {
          ...prev,
          service: {
            ...prev.service,
            selectedRoomTypes: nextRoomIds,
            roomTypeOptions: nextServiceRoomTypeOptions,
          },
          pricingSurcharge: {
            ...prev.pricingSurcharge,
            selectedRoomTypes: nextRoomIds,
            roomTypeOptions: nextPricingRoomTypeOptions,
          },
        };
      });
    }
  }, []);

  const generateRandomBarcode = useCallback(async () => {
    try {
      const response = await httpService.get(
        "vendorservice/generateVendorServiceBarcode",
      );
      return response.data;
    } catch (error) {
      toast.error("Failed to generate service barcode.");
      return null; // Return null on failure
    }
  }, []);

  const [formData, setFormData] = useState({
    service: {
      name: "",
      barcode: null,
      category: {
        service_category_id: "",
        service_type_id: "",
        service_type_name: "",
        service_category_type_id: "",
        type_id: "",
        service_category_name: "",
      },
      pet_type: [],
      basicPrice: "",
      description: "",
      bookable_online: "yes",
      images: [],
      selectedRoomTypes: [],
      roomTypeOptions: {},
    },
    // inclusion: {},
    adons: {
      servicesIncluded: [],
      addons: [],
      products: [],
      inclusions: {},
    },
    pricingSurcharge: {
      priceBasedOnSize: "no",
      priceBasedOnRoomType: "yes",
      priceDiscountForLongStay: "no",
      priceDiscountForMulitpleBoarding: "no",
      priceDiscountForClientLabel: "no",
    },
    review: {},
  });

  // This useEffect fetches the barcode from the API when creating a new service.
  useEffect(() => {
    if (isOpen && !roomId) {
      const fetchAndSetBarcode = async () => {
        const newBarcode = await generateRandomBarcode();
        if (newBarcode) {
          setFormData((prev) => ({
            ...prev,
            service: {
              ...prev.service,
              barcode: newBarcode,
            },
          }));
        }
      };
      fetchAndSetBarcode();
    }
  }, [isOpen, roomId, generateRandomBarcode]);

  const resetFormData = () => {
    setFormData({
      service: {
        name: "",
        barcode: null,
        category: {
          service_category_id: "",
          service_type_id: "",
          service_type_name: "",
          service_category_type_id: "",
          type_id: "",
          service_category_name: "",
        },
        pet_type: [],
        basicPrice: "",
        taxable: "no",
        taxPercentage: "",
        description: "",
        bookable_online: "yes",
        images: [],
        selectedRoomTypes: [],
        roomTypeOptions: {},
      },
      // inclusion: {},
      adons: {},
      pricingSurcharge: {
        priceBasedOnSize: "no",
        priceBasedOnRoomType: "yes",
        priceDiscountForLongStay: "no",
        priceDiscountForMulitpleBoarding: "no",
        priceDiscountForClientLabel: "no",
      },
      review: {},
    });
  };

  const resetAllStates = () => {
    resetFormData();
    setHasUnsavedChanges(false);
    setShowConfirmDialog(false);
    setIsSaving(false);
    // setServiceInclusions([]);
    setAdonServices([]);
    setCurrentTab("service");
    resetTabs();
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      resetAllStates();
      resetTabs();
      onClose();
    }
  };

  const confirmClose = () => {
    setShowConfirmDialog(false);
    resetAllStates();
    resetTabs();
    onClose();
  };
  useEffect(() => {
    if (roomId && isOpen) {
      // Simulate API call or load logic
      fetchServiceData();
      setTabProgress({
        service: 100,
        // inclusion: 100,
        // adons: 100,
        pricingSurcharge: 100,
        review: 100,
      });
    }
  }, [roomId, isOpen]);

  // Fetch service inclusions when needed
  // useEffect(() => {
  //   if (isOpen && formData.service.category) {
  //     setServiceCategory(formData.service.category);
  //     // fetchAdonServices();
  //   }
  // }, [isOpen, formData.service.category]);

  useEffect(() => {
    if (isOpen) {
      const fetchAllServiceNames = async () => {
        try {
          const response = await httpService.get(
            "vendorroomservice/getRoomServices",
          );
          const names = response.data.map((service) => ({
            name: service.name.trim().toLowerCase(),
            _id: service._id,
          }));
          setExistingServiceNames(names);
        } catch (err) {
          console.error("Failed to fetch all service names", err);
        }
      };
      fetchAllServiceNames();
    }
  }, [isOpen]);

  // useEffect(() => {
  //   const serviceType =
  //     formData.service.category?.service_type_name?.toLowerCase();
  //   if (serviceType?.includes("boarding")) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       adons: { ...prev.adons, products: [] },
  //     }));
  //   }
  // }, [formData.service.category?.service_type_name]);

  // Reset category when drawer closes
  // useEffect(() => {
  //   if (!isOpen) {
  //     setServiceCategory("");
  //   }
  // }, [isOpen]);

  function transformedPriceSurcharge(data) {
    const {
      size_surcharges,
      room_type_surcharges,
      long_stay_discount,
      multiple_boarding_discount,
      client_label_discount,
    } = data;

    return {
      // flags
      priceBasedOnSize: size_surcharges.enabled,
      priceBasedOnRoomType: "yes",
      priceDiscountForLongStay: long_stay_discount.enabled,
      priceDiscountForMulitpleBoarding: multiple_boarding_discount.enabled,
      priceDiscountForClientLabel: client_label_discount.enabled,

      // maps
      sizeOptions: size_surcharges.options.reduce((map, { size, price }) => {
        map[size] = String(price);
        return map;
      }, {}),

      roomTypeOptions: room_type_surcharges.options.reduce(
        (map, { room_type_id, price }) => {
          map[room_type_id] = String(price);
          return map;
        },
        {},
      ),

      selectedRoomTypes: room_type_surcharges.options.map(
        (opt) => opt.room_type_id,
      ),

      // passthrough arrays
      longStayOptions: long_stay_discount.options.map((opt) => ({
        no_of_day: opt.no_of_day,
        discount_type: opt.discount_type,
        discount: opt.discount,
      })),

      multipleBoardingDiscounts: multiple_boarding_discount.options.map(
        (opt) => ({
          no_of_pet: opt.no_of_pet,
          discount_type: opt.discount_type,
          discount: opt.discount,
        }),
      ),

      clientLabelDiscounts: client_label_discount.options.map((opt) => ({
        label: opt.label,
        discount_type: opt.discount_type,
        discount: opt.discount,
      })),
    };
  }

  const fetchServiceData = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `vendorroomservice/getRoomService/${roomId}`,
      );
      const data = response.data; // Get the actual service data from the response

      if (!data) {
        toast.error("Could not find the room service to edit");
        return;
      }

      const pricingSurchargeData = transformedPriceSurcharge(data);

      // Add tax initialization here
      const taxable = data.tax_percentage > 0 ? "yes" : "no";
      const taxPercentage = data.tax_percentage?.toString() || "";

      // Format service data
      const roomData = {
        name: data.name,
        barcode: data.barcode,
        // category: data.service_category,
        pet_type: Array.isArray(data.pet_type)
          ? data.pet_type
          : data.pet_type
            ? [data.pet_type]
            : [],
        bookable_online: data?.bookable_online,
        basicPrice: data?.basic_price,
        taxable: data.taxable || (data.tax_percentage > 0 ? "yes" : "no"),
        taxPercentage: data.tax_percentage?.toString() || "",
        images: data?.images,
        description: data.description || "",
        selectedRoomTypes: pricingSurchargeData.selectedRoomTypes || [],
        roomTypeOptions: pricingSurchargeData.roomTypeOptions || {},
      };
      // Format add-ons data
      const adonsData = {
        addons:
          data.addons.map((item) => ({
            _id: item.adon_id,
            name: item.name,
            value: item.value, // Default to Free, can be modified based on requirements
            name: item.name,
          })) || [],
        inclusions: data?.service_inclusions || {},
      };

      const formattedData = {
        service: roomData,
        // inclusion: data.inclusions,
        // adons: adonsData,
        review: {},
        pricingSurcharge: pricingSurchargeData,
      };

      setFormData(formattedData);
    } catch (err) {
      console.error("Failed to fetch service data", err);
      toast.error("Could not load service details");
    } finally {
      setLoading(false);
    }
  };

  const handleDataChange = () => {
    setHasUnsavedChanges(true);
  };

  // 1) Parent‐side validator
  // 1) Parent‐side validator
  const validateService = useCallback((svc) => {
    const errs = {};

    // Name
    if (!svc.name?.trim()) errs.name = "Service Name is required";
    else if (svc.name.length < 3)
      errs.name = "Name must be at least 3 characters";

    // Pet Type
    if (!Array.isArray(svc.pet_type) || svc.pet_type.length === 0) {
      errs.pet_type = "Pet Type is required";
    }

    // Room Selection
    if (!svc.selectedRoomTypes || svc.selectedRoomTypes.length === 0) {
      errs.rooms = "At least one room is required";
    } else if (Array.isArray(svc.pet_type) && svc.pet_type.length > 0) {
      // ✅ FIX: Check if roomTypeOptions is completely empty or just holds price values (e.g., {"room_id": "100"})
      // If we don't have the full room metadata (with .pet_type arrays), we CANNOT validate pet type compatibility.
      // In this case, we MUST skip this specific check to prevent false-positives blocking the user.
      const hasFullRoomMetadata =
        Object.values(svc.roomTypeOptions || {}).some(
          (room) =>
            room && typeof room === "object" && Array.isArray(room.pet_type),
        ) ||
        (Array.isArray(svc.roomTypeOptions) &&
          svc.roomTypeOptions.some((room) => Array.isArray(room.pet_type)));

      if (hasFullRoomMetadata) {
        const missingPetTypes = svc.pet_type.filter((pt) => {
          const ptId = String(pt?.pet_type_id || pt?._id || pt?.id || pt);

          const hasMatchingRoom = svc.selectedRoomTypes.some((selectedRoom) => {
            // A. SelectedRoom is a full object
            if (selectedRoom && typeof selectedRoom === "object") {
              const roomPets = selectedRoom.pet_type || [];
              return roomPets.some((roomPt) => {
                const rPtId = String(
                  roomPt.id || roomPt._id || roomPt.pet_type_id || roomPt,
                );
                return rPtId === ptId;
              });
            }

            // B. SelectedRoom is an ID
            const roomIdStr = String(selectedRoom);
            let roomObj = svc.roomTypeOptions?.[roomIdStr];

            if (!roomObj && Array.isArray(svc.roomTypeOptions)) {
              roomObj = svc.roomTypeOptions.find(
                (r) => String(r._id || r.id) === roomIdStr,
              );
            }

            // If metadata is missing for this specific room, skip validation for it
            if (
              !roomObj ||
              typeof roomObj !== "object" ||
              !Array.isArray(roomObj.pet_type)
            ) {
              return true;
            }

            const roomPets = roomObj.pet_type || [];
            return roomPets.some((roomPt) => {
              const rPtId = String(
                roomPt.id || roomPt._id || roomPt.pet_type_id || roomPt,
              );
              return rPtId === ptId;
            });
          });

          return !hasMatchingRoom;
        });

        if (missingPetTypes.length > 0) {
          const names = missingPetTypes
            .map((p) => p.name || "selected types")
            .join(" and ");
          errs.rooms = `Please select at least one room that supports ${names}`;
        }
      }
    }

    // Pricing
    if (!svc.basicPrice) errs.basicPrice = "Basic Price is required";
    if (Number(svc.basicPrice) === 0)
      errs.basicPrice = "Basic Price cannot be zero";

    return errs;
  }, []);

  const transformFormDataToPayload = () => {
    const { service, adons, pricingSurcharge } = formData;
    const inclusions = adons?.inclusions;
    const serviceSelectedRoomTypes = Array.isArray(service.selectedRoomTypes)
      ? service.selectedRoomTypes
      : [];
    const pricingSelectedRoomTypes = Array.isArray(
      pricingSurcharge.selectedRoomTypes,
    )
      ? pricingSurcharge.selectedRoomTypes
      : [];
    const pricingRoomTypeOptions = pricingSurcharge.roomTypeOptions || {};

    // Add these lines to extract tax info from service data
    const taxable = service.taxable === "yes";
    const tax_percentage = taxable ? Number(service.taxPercentage) : 0;

    // 1. mapping: [ outputKey,         flagProp,          optionsProp ]
    const surchargeConfig = [
      ["size_surcharges", "priceBasedOnSize", "sizeOptions"],
      ["room_type_surcharges", "priceBasedOnRoomType", "roomTypeOptions"],
      ["long_stay_discount", "priceDiscountForLongStay", "longStayOptions"],
      [
        "multiple_boarding_discount",
        "priceDiscountForMulitpleBoarding",
        "multipleBoardingDiscounts",
      ],
      [
        "client_label_discount",
        "priceDiscountForClientLabel",
        "clientLabelDiscounts",
      ],
    ];

    // 2. init everything with the correct enabled flag + empty options
    const pricingSurchargeData = surchargeConfig.reduce(
      (acc, [key, flagProp]) => {
        acc[key] = {
          enabled: pricingSurcharge[flagProp], // will be "yes" or "no"
          options: [],
        };
        return acc;
      },
      {},
    );

    // 3. for **each** config entry, if its flagProp === "yes", copy in the optionsProp
    surchargeConfig.forEach(([key, flagProp, optionsProp]) => {
      if (pricingSurcharge[flagProp] === "yes") {
        let raw = pricingSurcharge[optionsProp] || [];
        if (key === "room_type_surcharges") {
          // Rooms now live on the Service tab. Prefer service selection,
          // fall back to whatever the pricing tab had (for edit/legacy data).
          const roomIds =
            serviceSelectedRoomTypes.length > 0
              ? serviceSelectedRoomTypes
              : pricingSelectedRoomTypes;

          raw = roomIds.map((room_type_id) => {
            const id = String(room_type_id);
            return {
              room_type_id: id,
              price:
                pricingRoomTypeOptions[id] ||
                formData.service.roomTypeOptions?.[id] ||
                "0",
            };
          });
        } else {
          raw = pricingSurcharge[optionsProp] || [];
        }
        // Custom transform only for sizeOptions if it's in object form
        if (key === "size_surcharges" && !Array.isArray(raw)) {
          raw = Object.entries(raw).map(([size, price]) => ({ size, price }));
        }

        // if (key === "room_type_surcharges" && !Array.isArray(raw)) {
        //   // Convert object { room_type_id: price } to array [{ room_type_id, price }]
        //   raw = Object.entries(raw).map(([room_type_id, price]) => ({
        //     room_type_id,
        //     price: price || "0",
        //   }));
        // }
        pricingSurchargeData[key].options = raw;
      }
    });

    // Resolve pet type IDs (prefer `service.pet_type`, fallback to `service.petType`)
    let petTypeIds = [];
    if (Array.isArray(service.pet_type) && service.pet_type.length > 0) {
      petTypeIds = service.pet_type.map((pt) =>
        typeof pt === "object" ? pt.pet_type_id || pt._id : pt,
      );
    } else if (Array.isArray(service.petType) && service.petType.length > 0) {
      petTypeIds = service.petType.map((pt) =>
        typeof pt === "object" ? pt.pet_type_id || pt._id : pt,
      );
    }

    // Transform add-on services
    const adon_services = (adons?.addons || []).map((adonId) => ({
      adon_id: adonId?._id,
      value: adonId?.value, // Default to Free, can be modified based on requirements
      name: adonId?.name,
    }));

    // Transform images
    const images = service?.images || [];

    return {
      name: service.name,
      description: service.description,
      barcode: service.barcode,
      service_category: null,
      pet_type: petTypeIds,
      basic_price: service.basicPrice,
      taxable: service.taxable,
      tax_percentage: tax_percentage,
      bookable_online: service.bookable_online,
      service_inclusions: inclusions,
      addons: adon_services,
      images,
      ...pricingSurchargeData,
    };
  };

  const handleSubmit = async () => {
    const errs = validateService(formData.service);
    if (Object.keys(errs).length) {
      toast.error(
        <div className="flex flex-col" style={{ whiteSpace: "pre-line" }}>
          {Object.values(errs).map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </div>,
      );
      return;
    }
    setIsSaving(true);
    const payload = transformFormDataToPayload();
    // return;

    try {
      if (roomId) {
        await httpService.put(
          `vendorroomservice/updateRoomService/${roomId}`,
          payload,
        );
        toast.success("Boarding Service updated successfully");
      } else {
        await httpService.post("vendorroomservice/addRoomService", payload);
        toast.success("Boarding Service created successfully");
        if (vendorId) {
          await markSetupStepCompleteById(vendorId, "service");
        }
      }
      resetAllStates();
      resetTabs();
      if (onSaved) {
        onSaved();
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Save failed", err);
      toast.error(
        err.response?.data?.message || "Failed to save Boarding Service",
      );
    } finally {
      setIsSaving(false);
    }
  };

  //tabs code
  // Memoize tabs array to prevent unnecessary re-renders
  const tabs = useMemo(
    () => [
      {
        id: "service",
        label: "Service Details",
        icon: <FiFileText className="w-5 h-5" />,
      },
      // {
      //   id: "inclusion",
      //   label: "Service Inclusions",
      //   icon: <FiCheckCircle className="w-5 h-5" />,
      // },
      {
        id: "pricingSurcharge",
        label: "Pricing Surcharge",
        icon: <FaMoneyBillTrendUp className="w-5 h-5" />,
      },
      // {
      //   id: "adons",
      //   label: "Service Inclusions",
      //   icon: <FiPlusCircle className="w-5 h-5" />,
      // },
      {
        id: "review",
        label: "Review & Submit",
        icon: <FiCheckCircle className="w-5 h-5" />,
      },
    ],
    [],
  );

  // Memoize handlers
  // Find and modify the handleNext function
  const handleNext = useCallback(() => {

    if (currentStep < tabs.length - 1) {
      const errs = validateService(formData.service);

      if (Object.keys(errs).length) {
        // summary toast
        toast.error(
          <div className="flex flex-col" style={{ whiteSpace: "pre-line" }}>
            {Object.values(errs).map((msg, i) => (
              <div key={i}>{msg}</div>
            ))}
          </div>,
        );
        return;
      }
      if (activeTab === "pricingSurcharge") {
        if (!isPricingSurchargeFormValid) {
          toast.error("Please select discount type correctly");
          return;
        }
        const { priceBasedOnRoomType, selectedRoomTypes } =
          formData.pricingSurcharge;
        if (
          priceBasedOnRoomType === "yes" &&
          (!selectedRoomTypes || selectedRoomTypes.length === 0)
        ) {
          toast.error("Please select at least one room");
          return;
        }
      }
      if (activeTab === "service") {
        // Duplicate name check only (validation already done above by validateService)
        const currentServiceName = formData.service.name.trim().toLowerCase();
        const isDuplicate = existingServiceNames.some(
          (service) =>
            service.name === currentServiceName && service._id !== roomId,
        );
        if (isDuplicate) {
          toast.error(
            "A service with this name already exists. Please choose a different name.",
          );
          return;
        }
      }
      const currentTabId = tabs[currentStep].id;
      setTabProgress((prev) => ({
        ...prev,
        [currentTabId]: 100,
      }));
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setActiveTab(tabs[nextStep].id);
      handleTabChange(tabs[nextStep].id);
    }
  }, [
    currentStep,
    activeTab,
    isServiceFormValid,
    isPricingSurchargeFormValid,
    tabs,
    handleTabChange,
    formData.service.name,
    existingServiceNames,
    roomId,
  ]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setActiveTab(tabs[prevStep].id);
      handleTabChange(tabs[prevStep].id);
    }
  }, [currentStep, tabs, handleTabChange]);

  const handleTabClick = useCallback(
    (tabId, index) => {
      // Allow navigation to any tab if in edit mode (roomId exists)
      if (roomId) {
        setActiveTab(tabId);
        setCurrentStep(index);
        handleTabChange(tabId);
        return;
      }

      // Existing logic for new forms
      if (
        activeTab === "service" &&
        index > currentStep &&
        !isServiceFormValid
      ) {
        toast.error("Please fill in the required Boarding Service details");
        return;
      }

      if (
        tabId === "pricingSurcharge" &&
        !isPricingSurchargeFormValid &&
        roomId
      ) {
        toast.error(
          "Please complete all discount fields correctly before proceeding",
        );
        return;
      }

      const isDisabled =
        index > currentStep && tabProgress[tabs[currentStep].id] !== 100;
      if (!isDisabled) {
        setActiveTab(tabId);
        setCurrentStep(index);
        handleTabChange(tabId);
      }
    },
    [
      activeTab,
      currentStep,
      isServiceFormValid,
      isPricingSurchargeFormValid,
      tabProgress,
      tabs,
      handleTabChange,
      roomId,
    ],
  );

  const updateProgress = useCallback((tabId, progress) => {
    setTabProgress((prev) => ({
      ...prev,
      [tabId]: progress,
    }));
  }, []);

  // Memoize form data update handlers
  const handleRoomFormUpdate = useCallback(
    (data) => {
      setFormData((prev) => ({ ...prev, service: data }));
      handleDataChange();
      updateProgress("service", 100);
    },
    [setFormData, handleDataChange, updateProgress],
  );

  // const handleInclusionFormUpdate = useCallback(
  //   (data) => {
  //     setFormData((prev) => ({ ...prev, inclusion: data }));
  //     handleDataChange();
  //     updateProgress("inclusion", 100);
  //   },
  //   [setFormData, handleDataChange, updateProgress]
  // );

  const handleAdonsFormUpdate = useCallback(
    (data) => {
      setFormData((prev) => ({ ...prev, adons: data }));
      handleDataChange();
      updateProgress("adons", 100);
    },
    [setFormData, handleDataChange, updateProgress],
  );
  const handlePricingSurchargeFormUpdate = useCallback(
    (data) => {
      setFormData((prev) => ({ ...prev, pricingSurcharge: data }));
      handleDataChange();
      updateProgress("pricingSurcharge", 100);
    },
    [setFormData, handleDataChange, updateProgress],
  );

  const handleReviewFormUpdate = useCallback(() => {
    handleDataChange();
    updateProgress("review", 100);
    // Close the drawer after successful submission
    resetTabs();
  }, [handleDataChange, updateProgress]);

  const resetTabs = () => {
    setCurrentStep(0);
    setActiveTab("service");
    setIsServiceFormValid(false);
    setTabProgress({
      service: 0,
      // inclusion: 0,
      // adons: 0,
      pricingSurcharge: 0,
      review: 0,
    });
  };
  // Memoize tab content
  const tabContent = useMemo(() => {
    switch (activeTab) {
      case "service":
        return (
          <RoomForm
            isActive={activeTab === "service"}
            // setServiceCategory={setServiceCategory}
            data={formData.service}
            updateData={handleRoomFormUpdate}
            onValidationChange={setIsServiceFormValid}
            onAddRoom={handleAddRoom}
            roomListVersion={roomListVersion}
            roomId={roomId}
          />
        );
      // case "inclusion":
      //   return (
      //     <RoomInclusionForm
      //       isActive={activeTab === "inclusion"}
      //       serviceCategory={serviceCategory}
      //       data={formData.inclusion}
      //       updateData={handleInclusionFormUpdate}
      //     />
      //   );
      case "pricingSurcharge":
        return (
          <PriceSurchargeForm
            isActive={activeTab === "pricingSurcharge"}
            // serviceCategory={serviceCategory}
            data={formData.pricingSurcharge}
            updateData={handlePricingSurchargeFormUpdate}
            onValidationChange={setIsPricingSurchargeFormValid}
            selectedPetTypes={formData.service.pet_type}
            serviceSelectedRoomTypes={formData.service.selectedRoomTypes}
            serviceRoomTypeOptions={formData.service.roomTypeOptions}
            sizeCoatSelection={
              profileData?.sizeCoatSelection || "size_and_coat"
            }
          />
        );
      // case "adons":
      //   return (
      //     <RoomAdonsForm
      //       isActive={activeTab === "adons"}
      //       // serviceCategory={serviceCategory}
      //       data={{
      //         ...formData.adons,
      //         petType: formData.service.pet_type, // Pass the pet_type from service form
      //       }}
      //       updateData={handleAdonsFormUpdate}
      //     />
      //   );
      case "review":
        return (
          <ReviewForm
            isActive={activeTab === "review"}
            data={formData}
            onDataChange={handleReviewFormUpdate}
            adonServices={adonServices}
          />
        );
      default:
        return null;
    }
  }, [
    activeTab,
    roomId,
    // serviceCategory,
    formData,
    handleRoomFormUpdate,
    // handleInclusionFormUpdate,
    setIsPricingSurchargeFormValid,
    handleAdonsFormUpdate,
    handleReviewFormUpdate,
    adonServices,
    handlePricingSurchargeFormUpdate,
  ]);

  return (
    <>
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-[10020] bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Unsaved Changes
            </h3>
            <p className="text-gray-600 mb-6">
              You have unsaved changes. Are you sure you want to close?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmClose}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Close anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {isRoomTypeDrawerOpen &&
        createPortal(
          <RoomTypeDrawer
            isOpen={isRoomTypeDrawerOpen}
            roomId={null}
            onClose={() => setIsRoomTypeDrawerOpen(false)}
            onSaved={handleRoomSaved}
          />,
          document.body,
        )}

      {/* Main Drawer */}
      <div
        className={`fixed inset-0 z-[10010] transition-all duration-300 ease-in-out ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
          onClick={handleClose}
        />
        <div
          className={`absolute right-0 top-0 h-full w-full lg:w-3/4 2xl:w-1/2 bg-white shadow-xl transform transition-transform duration-300 ease-out ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Loading Skeleton */}
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-90 z-10">
              <div className="p-4 space-y-4 animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="mt-8 space-y-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex justify-start items-center p-2 border-b bg-white shadow-sm gap-2">
            <button
              onClick={handleClose}
              className="flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <HiArrowSmLeft className="w-8 h-8" />
            </button>
            <h2 className="text-2xl font-bold text-blue-900">
              {!roomId ? "Add" : "Edit"} Boarding Service
            </h2>
          </div>

          {/* Content */}
          {isOpen && (
            <>
              <div className="flex h-[calc(100vh-110px)]">
                <div className="w-1/4 p-4 border-r border-gray-200 bg-gray-50">
                  <div className="flex flex-col space-y-3">
                    {tabs.map((tab, index) => {
                      const isActive = tab.id === activeTab;
                      const isCompleted = tabProgress[tab.id] === 100;
                      const isDisabled = index > currentStep && !isCompleted;
                      const isClickDisabled =
                        !roomId &&
                        (isDisabled ||
                          (tab.id !== "service" &&
                            currentStep === 0 &&
                            !isServiceFormValid));

                      return (
                        <div
                          key={tab.id}
                          className={`group relative ${
                            isClickDisabled
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                          onClick={() =>
                            !isClickDisabled && handleTabClick(tab.id, index)
                          }
                        >
                          <div
                            className={`flex items-center p-3 rounded-lg transition-all duration-200 ${
                              isActive
                                ? "bg-blue-50 border-blue-500 shadow-sm"
                                : "hover:bg-gray-100"
                            }`}
                          >
                            <div
                              className={`w-10 h-10 flex items-center justify-center rounded-full mr-3 ${
                                isActive
                                  ? "bg-blue-500 text-white"
                                  : isCompleted
                                    ? "bg-green-500 text-white"
                                    : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              {isCompleted ? (
                                <FiCheck className="w-6 h-6" />
                              ) : (
                                tab.icon
                              )}
                            </div>
                            <div className="flex-1">
                              <h3
                                className={`font-medium ${
                                  isActive ? "text-blue-900" : "text-gray-900"
                                }`}
                              >
                                {tab.label}
                              </h3>
                              <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full transition-all duration-500 ${
                                    isCompleted
                                      ? "bg-green-500"
                                      : isActive
                                        ? "bg-blue-500"
                                        : "bg-gray-300"
                                  }`}
                                  style={{ width: `${tabProgress[tab.id]}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="w-3/4 p-4 space-y-4 overflow-auto bg-white">
                  <div className="transition-opacity duration-200">
                    {tabContent}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="absolute w-full bottom-0 p-2 bg-white border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
            <div className="flex flex-wrap items-center justify-end gap-4">
              {currentTab !== "service" && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-[120px] px-4 py-2.5 border border-yellow-600 text-yellow-600 rounded-md hover:bg-yellow-50 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                >
                  Previous
                </button>
              )}

              {currentTab !== "review" && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-[120px] px-4 py-2.5 bg-primary text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Next
                </button>
              )}

              {/* {currentTab === "review" && ( */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="min-w-[120px] px-4 py-2.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  `${roomId ? "Update" : "Save"} Boarding Service`
                )}
              </button>
              {/* )} */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};;

export default RoomDrawer;
